import React from 'react';
import { Card, Descriptions, Spin, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getGuidelineById } from '@/api/guidelines';
import { useAuth } from '@/contexts/AuthContext';
import dayjs from 'dayjs';


interface GuidelineTabProps {
  guidelineId: string;
}

const GuidelineTab: React.FC<GuidelineTabProps> = ({ guidelineId }) => {
  const { t } = useTranslation(['guidelines', 'common']);
  const { firestoreUser } = useAuth();

  const { data: guideline, isLoading, error } = useQuery({
    queryKey: ['guideline', guidelineId],
    queryFn: () => getGuidelineById(guidelineId, firestoreUser!),
    enabled: !!guidelineId && !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error || !guideline) {
    return (
      <Card>
        <Empty description={t('guidelines:notFound')} />
      </Card>
    );
  }

  return (
    <Card>
      <Descriptions bordered column={1}>
        <Descriptions.Item label={t('guidelines:fields.version')}>
          {guideline.version}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('guidelines:fields.status')}>
          {t(`guidelines:status.${guideline.status}`)}
        </Descriptions.Item>
        
        <Descriptions.Item label={t('guidelines:fields.effectiveDate')}>
          {dayjs(guideline.effectiveDate).format('DD/MM/YYYY')}
        </Descriptions.Item>
        
        {guideline.expiryDate && (
          <Descriptions.Item label={t('guidelines:fields.expiryDate')}>
            {dayjs(guideline.expiryDate).format('DD/MM/YYYY')}
          </Descriptions.Item>
        )}
        
        {guideline.author && (
          <Descriptions.Item label={t('guidelines:fields.author')}>
            {guideline.author.name}
          </Descriptions.Item>
        )}
        
        <Descriptions.Item label={t('guidelines:fields.records')}>
          {guideline.records.length} {t('common:items')}
        </Descriptions.Item>
        
        {guideline.activityRecords && guideline.activityRecords.length > 0 && (
          <Descriptions.Item label={t('guidelines:fields.activityRecords')}>
            {guideline.activityRecords.length} {t('common:items')}
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
};

export default GuidelineTab; 